import React, { useEffect, useState } from 'react';
import ApiService from '../services/ApiService';
import { toast } from 'react-toastify';
import PaymentForm from './PaymentForm';
import { formatCurrency, formatDate } from '../utils/helper';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Link, useNavigate } from 'react-router-dom';
import TimeSlotSelect from './TimeSlotSelect';
import AuthService from '../services/AuthService';

const BookingFlow = ({ mode = 'guest' }) => {
    const isGuest = mode === 'guest';
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    const [branches, setBranches] = useState([]);
    const [services, setServices] = useState([]);

    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedTime, setSelectedTime] = useState('09:00');

    const [customerDetails, setCustomerDetails] = useState({
        name: '',
        email: '',
        phone: '',
        notes: '',
    });

    const [clientSecret, setClientSecret] = useState('');
    const [appointmentData, setAppointmentData] = useState(null);
    const [authToken, setAuthToken] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [isNewCustomer, setIsNewCustomer] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState(null);
    const [paymentRules, setPaymentRules] = useState(null);
    const [showTermsModal, setShowTermsModal] = useState(false);

    useEffect(() => {
        // Always try to load user data if available in localStorage
        const user = AuthService.getCurrentUser();
        if (user) {
            setCustomerDetails(prev => ({
                ...prev,
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
            }));
        }
    }, [isGuest]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [branchesRes, servicesRes] = await Promise.all([
                    ApiService.request({ method: 'GET', url: '/public/branches' }),
                    ApiService.request({ method: 'GET', url: '/public/services' }),
                ]);

                if (branchesRes.data.status) setBranches(branchesRes.data.data);
                if (servicesRes.data.status) setServices(servicesRes.data.data);
            } catch (error) {
                toast.error('Failed to load booking data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleServiceSelect = (serviceId) => {
        if (selectedServices.includes(serviceId)) {
            setSelectedServices(selectedServices.filter(id => id !== serviceId));
        } else {
            setSelectedServices([...selectedServices, serviceId]);
        }
    };

    const handleNextToDetails = () => {
        if (!selectedBranch) {
            toast.error('Please select a branch');
            return;
        }
        if (!selectedDate) {
            toast.error('Please select a date');
            return;
        }
        if (!selectedTime) {
            toast.error('Please select a time slot');
            return;
        }
        if (selectedServices.length === 0) {
            toast.error('Please select at least one service');
            return;
        }
        setStep(2);
        window.scrollTo(0, 0);
    };

    const handleBackToService = () => {
        setStep(1);
        window.scrollTo(0, 0);
    };

    const finalizeBooking = (token, customer) => {
        if (!isGuest) {
            return;
        }
        if (token && customer) {
            const user = {
                id: customer.id,
                name: customer.name,
                email: customer.email,
                role: 'customer',
                auth_token: token,
            };
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('token', token);
        }
    };

    const handleNextToPayment = async (e) => {
        e.preventDefault();

        if (isGuest) {
            if (!customerDetails.name || !customerDetails.email || !customerDetails.phone) {
                toast.error('Please fill all customer details');
                return;
            }
        }

        try {
            setLoading(true);
            
            // Adjust date for local timezone to prevent it being sent as previous day
            const date = new Date(selectedDate);
            date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
            const formattedDate = date.toISOString().split('T')[0];

            const payload = {
                branch_id: selectedBranch,
                service_ids: selectedServices,
                date: formattedDate,
                time: selectedTime,
                customer_name: customerDetails.name,
                customer_email: customerDetails.email,
                customer_phone: customerDetails.phone,
                notes: customerDetails.notes,
            };

            const response = await ApiService.request({
                method: 'POST',
                url: '/public/appointments',
                data: payload,
            });

            if (response.data.status) {
                const data = response.data.data;
                setClientSecret(data.clientSecret);
                setAppointmentData(data.appointment);
                setAuthToken(data.auth_token);
                setCustomerData(data.customer);
                setIsNewCustomer(data.is_new_customer);
                setGeneratedPassword(data.generated_password);
                setPaymentRules({
                    deposit_percentage: data.deposit_percentage,
                    free_cancellation_time: data.free_cancellation_time,
                    partial_cancellation_fee_time: data.partial_cancellation_fee_time,
                    partial_cancellation_fee_percentage: data.partial_cancellation_fee_percentage,
                    full_cancellation_fee_percentage: data.full_cancellation_fee_percentage,
                });

                if (data.clientSecret) {
                    setStep(3);
                } else {
                    finalizeBooking(data.auth_token, data.customer);
                    setStep(4);
                }
                window.scrollTo(0, 0);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Booking failed');
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentSuccess = async (paymentIntent) => {
        // We can optionally call an API to verify/finalize if not relying solely on webhook
        // But for now, we assume webhook or local confirmation is enough for UX
        // Actually, we SHOULD call an API to ensure the transaction is recorded if webhook is delayed or fails locally
        // And to update the appointment status immediately for the user.
        
        // Finalize local storage first so ApiService can use the token
        finalizeBooking(authToken, customerData);

        try {
            if (paymentIntent && authToken) {
                 await ApiService.request({
                    method: 'POST',
                    url: '/customer/confirm-payment',
                    data: {
                        payment_intent_id: paymentIntent.id,
                        appointment_id: appointmentData?.id
                    }
                });
            }
        } catch (e) {
            console.error("Manual confirmation failed, relying on webhook", e);
        }

        setStep(4);
        window.scrollTo(0, 0);

        // Redirect to appointments after 15 seconds so they can see account info if needed
        if (authToken) {
            setTimeout(() => {
                navigate('/appointments');
            }, 15000);
        }
    };

    const getServicePrice = () => {
        const selected = services.filter(s => selectedServices.includes(s.id));
        return selected.reduce((sum, s) => sum + parseFloat(s.price), 0);
    };

    const getDepositAmount = () => {
        if (appointmentData && appointmentData.deposit_amount) {
            return appointmentData.deposit_amount;
        }
        return getServicePrice();
    };

    const renderTermsSummary = () => {
        if (!paymentRules) return '';
        const deposit = paymentRules.deposit_percentage || 0;
        const freeTime = paymentRules.free_cancellation_time || 48;
        const partialTime = paymentRules.partial_cancellation_fee_time || 24;
        const partialFee = paymentRules.partial_cancellation_fee_percentage || 50;
        const fullFee = paymentRules.full_cancellation_fee_percentage || 100;

        return `I accept the booking terms: ${deposit}% deposit now. Free cancellation more than ${freeTime} hours before. ${partialFee}% fee between ${freeTime} and ${partialTime} hours. ${fullFee}% fee within ${partialTime} hours. My card may be charged accordingly.`;
    };

    if (loading && step === 1 && branches.length === 0) {
        return (
            <div className="d-flex justify-content-center py-10">
                <div className="spinner-border text-primary"></div>
            </div>
        );
    }

    return (
        <div className="container py-5">
            <div className="card shadow-lg border-0 mw-900px mx-auto rounded-4 overflow-hidden">
                <div className="card-header bg-white border-bottom border-light py-10">
                    <div className="mx-auto" style={{ maxWidth: '600px' }}>
                        <div className="position-relative">
                            <div
                                className="position-absolute start-0 w-100"
                                style={{
                                    top: '18px',
                                    height: '4px',
                                    backgroundColor: '#e4e6ef',
                                    zIndex: 0,
                                    borderRadius: '4px',
                                }}
                            ></div>

                            <div
                                className="position-absolute start-0 bg-base transition-all"
                                style={{
                                    top: '18px',
                                    height: '4px',
                                    width: `${(step - 1) * 50}%`,
                                    maxWidth: '100%',
                                    zIndex: 0,
                                    transition: 'width 0.4s ease',
                                    borderRadius: '4px',
                                }}
                            ></div>

                            <div className="d-flex justify-content-between position-relative gap-15" style={{ zIndex: 1 }}>
                                <div className="text-center bg-white px-2">
                                    <div
                                        className={`d-flex align-items-center justify-content-center rounded-circle border border-2 fw-bolder fs-4 mb-2 mx-auto ${
                                            step >= 1 ? 'border-base bg-base text-white' : 'border-secondary bg-white text-muted'
                                        }`}
                                        style={{ width: '40px', height: '40px', transition: 'all 0.3s' }}
                                    >
                                        {step > 1 ? <i className="fas fa-check text-white"></i> : '1'}
                                    </div>
                                    <div className={`fw-bold fs-7 ${step >= 1 ? 'text-base' : 'text-muted'}`}>Service</div>
                                </div>

                                <div className="text-center bg-white px-2">
                                    <div
                                        className={`d-flex align-items-center justify-content-center rounded-circle border border-2 fw-bolder fs-4 mb-2 mx-auto ${
                                            step >= 2 ? 'border-base bg-base text-white' : 'border-secondary bg-white text-muted'
                                        }`}
                                        style={{ width: '40px', height: '40px', transition: 'all 0.3s' }}
                                    >
                                        {step > 2 ? <i className="fas fa-check text-white"></i> : '2'}
                                    </div>
                                    <div className={`fw-bold fs-7 ${step >= 2 ? 'text-base' : 'text-muted'}`}>Details</div>
                                </div>

                                <div className="text-center bg-white px-2">
                                    <div
                                        className={`d-flex align-items-center justify-content-center rounded-circle border border-2 fw-bolder fs-4 mb-2 mx-auto ${
                                            step >= 3 ? 'border-base bg-base text-white' : 'border-secondary bg-white text-muted'
                                        }`}
                                        style={{ width: '40px', height: '40px', transition: 'all 0.3s' }}
                                    >
                                        {step > 3 ? <i className="fas fa-check text-white"></i> : '3'}
                                    </div>
                                    <div className={`fw-bold fs-7 ${step >= 3 ? 'text-base' : 'text-muted'}`}>Payment</div>
                                </div>

                               
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-body p-lg-10 p-5">
                    {step === 1 && (
                        <div>
                            <div className="mb-10">
                                <h3 className="fw-bolder text-dark mb-5">Select Branch</h3>
                                <div className="row g-4">
                                    {branches.map(branch => (
                                        <div className="col-md-6" key={branch.id}>
                                            <div
                                                className={`card h-100 cursor-pointer transition-all ${
                                                    selectedBranch == branch.id ? 'border-base bg-light-base shadow-sm' : 'border-dashed hover-elevate-up'
                                                }`}
                                                onClick={() => setSelectedBranch(branch.id)}
                                                style={{ borderWidth: '2px' }}
                                            >
                                                <div className="card-body d-flex align-items-center p-5">
                                                    <div
                                                        className={`symbol symbol-50px me-4 ${
                                                            selectedBranch == branch.id ? 'bg-base' : 'bg-light'
                                                        }`}
                                                    >
                                                        <span className="symbol-label">
                                                            <i
                                                                className={`fas fa-store fs-2 ${
                                                                    selectedBranch == branch.id ? 'text-base' : 'text-dark'
                                                                }`}
                                                            ></i>
                                                        </span>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="fw-bold text-gray-800 fs-5 mb-1">{branch.name}</div>
                                                        <div className="text-muted fs-7">{branch.address}</div>
                                                    </div>
                                                    {selectedBranch == branch.id && (
                                                        <i className="fas fa-check-circle text-base fs-2 ms-3"></i>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-10">
                                <h3 className="fw-bolder text-dark mb-5">Date & Time</h3>
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-gray-700">Select Date</label>
                                        <DatePicker
                                            selected={selectedDate}
                                            onChange={date => setSelectedDate(date)}
                                            className="form-control form-control-lg form-control-solid"
                                            dateFormat="yyyy-MM-dd"
                                            minDate={new Date()}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold text-gray-700">Select Time</label>
                                        <TimeSlotSelect
                                            value={selectedTime}
                                            onChange={val => setSelectedTime(val)}
                                            branchId={selectedBranch}
                                            date={selectedDate}
                                            apiPrefix="/public"
                                            size="lg"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-5">
                                <h3 className="fw-bolder text-dark mb-5">Select Service</h3>
                                <div className="row g-4">
                                    {services.map(service => (
                                        <div className="col-md-6" key={service.id}>
                                            <div
                                                    className={`card h-100 cursor-pointer transition-all ${
                                                        selectedServices.includes(service.id)
                                                            ? 'border-base bg-light-base shadow-sm'
                                                            : 'border-dashed hover-elevate-up'
                                                    }`}
                                                onClick={() => handleServiceSelect(service.id)}
                                                style={{ borderWidth: '2px' }}
                                            >
                                                <div className="card-body p-5">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <div className="badge badge-light-primary">
                                                            {service.duration} mins
                                                        </div>
                                                        <div className="fw-bolder text-base fs-4">
                                                            {formatCurrency(service.price)}
                                                        </div>
                                                    </div>
                                                    <div className="fw-bold fs-5 text-dark mb-1">{service.name}</div>
                                                    <div className="text-gray-500 fs-7">
                                                        {service.description || 'No description available'}
                                                    </div>

                                                    {selectedServices.includes(service.id) && (
                                                        <div className="position-absolute top-0 end-0 m-1">
                                                            <i className="fas fa-check-circle text-base fs-3"></i>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="d-flex justify-content-end pt-5">
                                <button className="btn btn-base btn-lg px-6" onClick={handleNextToDetails}>
                                    Next Step <i className="fas fa-arrow-right ms-2"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <form onSubmit={handleNextToPayment}>
                            <div className="text-center mb-10">
                                <h3 className="fw-bolder text-dark">Customer Details</h3>
                                <div className="text-muted fw-semibold fs-6">
                                    {isGuest ? 'Please provide your contact information' : 'Your details are prefilled from your account'}
                                </div>
                            </div>

                            <div className="row g-5 mb-10">
                                <div className="col-md-6">
                                    <label className="form-label required fw-semibold fs-6">Full Name</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-lg form-control-solid"
                                        placeholder="John Doe"
                                        value={customerDetails.name}
                                        onChange={e =>
                                            setCustomerDetails({ ...customerDetails, name: e.target.value })
                                        }
                                        required
                                        disabled={!isGuest}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label required fw-semibold fs-6">Email Address</label>
                                    <input
                                        type="email"
                                        className="form-control form-control-lg form-control-solid"
                                        placeholder="john@example.com"
                                        value={customerDetails.email}
                                        onChange={e =>
                                            setCustomerDetails({ ...customerDetails, email: e.target.value })
                                        }
                                        required
                                        disabled={!isGuest}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label required fw-semibold fs-6">Phone Number</label>
                                    <div className="input-group input-group-solid input-group-lg">
                                        <span className="input-group-text bg-light">+353</span>
                                        <input
                                            type="tel"
                                            className="form-control form-control-lg form-control-solid"
                                            placeholder="87 123 4567"
                                            value={customerDetails.phone.replace('+353', '')}
                                            onChange={e =>
                                                setCustomerDetails({
                                                    ...customerDetails,
                                                    phone: '+353' + e.target.value.replace(/\D/g, ''),
                                                })
                                            }
                                            required
                                            disabled={!isGuest}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label fw-semibold fs-6">Special Requests (Optional)</label>
                                    <textarea
                                        className="form-control form-control-lg form-control-solid"
                                        rows="3"
                                        placeholder="Any specific instructions..."
                                        value={customerDetails.notes}
                                        onChange={e =>
                                            setCustomerDetails({ ...customerDetails, notes: e.target.value })
                                        }
                                    ></textarea>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between pt-5">
                                <button
                                    type="button"
                                    className="btn btn-light btn-lg"
                                    onClick={handleBackToService}
                                >
                                    Back
                                </button>
                                <button type="submit" className="btn btn-base btn-lg" disabled={loading}>
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                    ) : (
                                        <>
                                            Proceed to Payment <i className="fas fa-credit-card ms-2"></i>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {step === 3 && clientSecret && (
                        <div>
                            <div className="text-center mb-10">
                                <h3 className="fw-bolder text-dark">Secure Payment</h3>
                                <div className="text-muted fw-semibold fs-6">
                                    Complete your booking by making a payment
                                </div>
                            </div>

                            <div className="row justify-content-center">
                                <div className="col-lg-8">
                                    <div className="bg-light p-5 rounded-3 mb-5">
                                        <div className="mb-3">
                                            <span className="text-gray-600 d-block mb-2">Services:</span>
                                            {services
                                                .filter(s => selectedServices.includes(s.id))
                                                .map(s => (
                                                    <div
                                                        key={s.id}
                                                        className="d-flex justify-content-between align-items-center mb-1 ps-3"
                                                    >
                                                        <span className="fw-bold">{s.name}</span>
                                                        <span>{formatCurrency(s.price)}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-gray-600">Date & Time:</span>
                                            <span className="fw-bold">
                                                {formatDate(selectedDate, 'MMM DD, YYYY')} at {selectedTime}
                                            </span>
                                        </div>
                                        <div className="separator my-4"></div>
                                        <div className="d-flex justify-content-between align-items-center fs-5 mb-2">
                                            <span className="fw-bold text-dark">Total Amount:</span>
                                            <span className="fw-bolder text-primary">
                                                {formatCurrency(getServicePrice())}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center fs-6">
                                            <span className="text-gray-700">Deposit Due Today:</span>
                                            <span className="fw-bold text-dark">
                                                {formatCurrency(getDepositAmount())}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="bookingTermsCheckbox"
                                                checked={!!paymentRules}
                                                readOnly
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="bookingTermsCheckbox"
                                            >
                                                {paymentRules
                                                    ? 'By continuing you accept our deposit and cancellation terms.'
                                                    : 'Loading terms...'}
                                            </label>
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm ms-2 p-0 align-baseline"
                                                onClick={() => setShowTermsModal(true)}
                                            >
                                                View details
                                            </button>
                                        </div>
                                    </div>

                                    <PaymentForm
                                        amount={getDepositAmount()}
                                        onSuccess={handlePaymentSuccess}
                                        apiPrefix="/public"
                                        appointmentId={appointmentData?.id}
                                        agreementText={renderTermsSummary()}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="text-center py-10">
                            <div className="mb-5">
                                <i className="fas fa-check-circle text-success fs-5x"></i>
                            </div>
                            <h1 className="fw-bolder text-dark mb-5">Booking Confirmed!</h1>
                            <p className="text-gray-600 fs-5 mb-10">
                                Thank you for your booking. A confirmation has been sent to your email.
                                <br />
                                Your appointment is scheduled for{' '}
                                <strong>
                                    {formatDate(selectedDate, 'MMM DD, YYYY')} at {selectedTime}
                                </strong>
                                .
                            </p>

                            {isGuest && isNewCustomer && generatedPassword && (
                                <div className="alert alert-dismissible bg-light-warning border border-warning d-flex flex-column flex-sm-row p-5 mb-10 mx-auto mw-600px">
                                    <div className="d-flex flex-column text-start pe-0 pe-sm-10">
                                        <h5 className="mb-1">Account Created</h5>
                                        <span>
                                            Your account has been created. Password:{' '}
                                            <strong>{generatedPassword}</strong>
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="d-flex justify-content-center gap-3">
                                {isGuest ? (
                                    <>
                                        <a
                                            href={process.env.REACT_APP_BASE_PATH || '' + '/appointments'}
                                            className="btn btn-base btn-lg"
                                        >
                                            Go to My Appointments
                                        </a>
                                        <Link to="/" className="btn btn-light btn-lg">
                                            Book Another
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/appointments"
                                            className="btn btn-base btn-lg"
                                        >
                                            View My Appointments
                                        </Link>
                                        <Link to="/" className="btn btn-light btn-lg">
                                            Book Another
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {showTermsModal && paymentRules && (
                        <div
                            className="modal fade show d-block"
                            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        >
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Booking Terms & Conditions</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setShowTermsModal(false)}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <p className="mb-3">
                                            A deposit of {paymentRules.deposit_percentage || 0}% of the total
                                            appointment fee is taken at the time of booking. Your card
                                            details are stored securely for future charges related to this
                                            appointment.
                                        </p>
                                        <p className="mb-3">
                                            If you cancel or do not show up more than{' '}
                                            {paymentRules.free_cancellation_time || 48} hours before your
                                            appointment time, your deposit will be fully refunded.
                                        </p>
                                        <p className="mb-3">
                                            If you cancel or do not show up within{' '}
                                            {paymentRules.free_cancellation_time || 48} hours but more than{' '}
                                            {paymentRules.partial_cancellation_fee_time || 24} hours before your
                                            appointment time, a{' '}
                                            {paymentRules.partial_cancellation_fee_percentage || 50}% cancellation
                                            fee may be charged.
                                        </p>
                                        <p className="mb-3">
                                            If you cancel or do not show up within{' '}
                                            {paymentRules.partial_cancellation_fee_time || 24} hours of your
                                            appointment time, a{' '}
                                            {paymentRules.full_cancellation_fee_percentage || 100}% cancellation
                                            fee may be charged.
                                        </p>
                                        <p className="mb-0">
                                            By proceeding with payment you authorise these charges to be
                                            taken from your saved card where applicable.
                                        </p>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setShowTermsModal(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookingFlow;

